import * as React from "react";
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { ROUTES } from '@/constants';
import { apiClient } from '@/lib/api';
import { NotFoundError } from '@/lib/errors';
import { useAuthStore } from '@store/authStore';
import Button from '@components/ui/Button';
import { Input } from '@components/ui/Input';
import { RiArrowRightLine } from "@remixicon/react";

// MFAVerifyForm component for reuse
export function MFAVerifyForm() {
  const navigate = useNavigate();
  const { setAuthType, setUser } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inputsRef = React.useRef([]);
  const [digits, setDigits] = React.useState(["", "", "", "", "", ""]);

  useEffect(() => {
    let mounted = true;
    let authSubscription = null;
    let sessionProcessed = false;

    const handleOAuthCallback = async () => {
      // Check for OAuth errors in URL first
      const urlParams = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const oauthError = urlParams.get('error') || hashParams.get('error');
      const errorDescription = urlParams.get('error_description') || hashParams.get('error_description');

      if (oauthError) {
        console.error('OAuth error detected:', oauthError, errorDescription);
        setError(`Authentication failed: ${decodeURIComponent(errorDescription || oauthError)}`);
        return;
      }

      // Check if this is an OAuth callback (hash in URL)
      if (window.location.hash) {
        console.log('MFAVerify: OAuth callback detected, hash:', window.location.hash.substring(0, 50));
        setLoading(true);

        // Listen for auth state changes to handle OAuth callback
        const { data } = supabase.auth.onAuthStateChange(async (event, session) => {
          if (!mounted || sessionProcessed) return;

          console.log('MFAVerify auth event:', event, 'Session exists:', !!session, 'User:', session?.user?.email);

          if (event === 'SIGNED_IN' && session?.user) {
            console.log('User signed in via OAuth, routing...');
            sessionProcessed = true;
            // OAuth sign-in successful, check MFA and profile status
            await routeUserAfterOAuth(session.user);
          }
        });

        authSubscription = data.subscription;

        // Also check immediately in case session already exists
        setTimeout(async () => {
          if (!mounted || sessionProcessed) return;

          console.log('MFAVerify: Checking for existing session...');
          const { data: { session }, error } = await supabase.auth.getSession();

          if (error) {
            console.error('Error getting session:', error);
          }

          console.log('MFAVerify session check:', !!session, 'User:', session?.user?.email);

          if (session?.user) {
            sessionProcessed = true;
            await routeUserAfterOAuth(session.user);
          } else {
            console.log('No session found after OAuth callback');
            setError('Authentication failed. Please try again.');
            setLoading(false);
          }
        }, 1000); // Increased timeout to 1 second
      }
    };

    const routeUserAfterOAuth = async (user) => {
      try {
        // Check MFA status
        const { data: factors } = await supabase.auth.mfa.listFactors();

        if (factors?.totp && factors.totp.length > 0) {
          const verifiedFactor = factors.totp.find(f => f.status === 'verified');
          if (verifiedFactor) {
            // MFA enrolled - check if profile exists
            try {
              await apiClient.getUserByUserId(user.id);
              // Profile exists, go to dashboard
              navigate('/dashboard');
            } catch (err) {
              if (err instanceof NotFoundError || err.statusCode === 404) {
                // No profile, go to profile setup
                navigate('/profile-setup');
              } else {
                throw err;
              }
            }
          } else {
            // Unverified MFA, go to enrollment
            navigate('/mfa-enroll');
          }
        } else {
          // No MFA, go to enrollment
          navigate('/mfa-enroll');
        }
      } catch (err) {
        console.error('Error routing user:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    handleOAuthCallback();

    return () => {
      mounted = false;
      if (authSubscription) {
        authSubscription.unsubscribe();
      }
    };
  }, [navigate]);

  const verifyMFASignIn = async (e) => {
    e.preventDefault();
    setError(null);

    if (digits.some(d => d === "")) {
      setError("Please enter a 6-digit code");
      return;
    }

    const mfaCode = digits.join("");
    setLoading(true);
    
    try {
      const factors = await supabase.auth.mfa.listFactors();
      console.log('factors', factors);
      if (factors.data && factors.data.totp.length > 0) {
        const factorId = factors.data.totp[0].id;

        const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
          factorId,
        });

        if (challengeError) throw challengeError;

        if (challengeData) {
          const { error } = await supabase.auth.mfa.verify({
            factorId,
            challengeId: challengeData.id,
            code: mfaCode,
          });

          if (error) throw error;

          // Refresh session to ensure token is available with AAL2
          const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
          if (refreshError) {
            console.error("Error refreshing session after MFA:", refreshError);
          }

          // Wait a moment for the session to propagate
          await new Promise(resolve => setTimeout(resolve, 300));

          // Check if user has completed profile
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            console.log("📱 MFAVerify: MFA successful, updating user in Zustand");
            setUser(user); // Save user to Zustand after successful MFA

            // Sync auth store to ensure API client has fresh token
            await useAuthStore.getState().syncWithSupabase();
            
            try {
              // Use getUserByUserId to check if current user has a profile
              await apiClient.getUserByUserId(user.id);
              // User profile exists in backend, go to dashboard
              navigate('/dashboard');
            } catch (err) {
              // Only redirect to profile setup if user profile doesn't exist (404)
              if (err instanceof NotFoundError || err.statusCode === 404) {
                navigate('/profile-setup');
              } else {
                // For other errors (network, server, etc.), throw to outer catch
                throw err;
              }
            }
          }
        }
      }
    } catch (err) {
      console.log("error", err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };


  const handleChange = (index, value) => {
    if (!/^\d?$/.test(value)) return; // Only allow single digit
    const newDigits = [...digits];
    newDigits[index] = value;
    setDigits(newDigits);

    // Move to next input if value entered
    if (value && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (digits[index] === "") {
        if (index > 0) {
          inputsRef.current[index - 1]?.focus();
        }
      } else {
        const newDigits = [...digits];
        newDigits[index] = "";
        setDigits(newDigits);
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputsRef.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const paste = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (paste.length === 6) {
      setDigits(paste.split(""));
      inputsRef.current[5]?.focus();
      e.preventDefault();
    }
  };

  return (
    <>
      <div className="text-center lg:text-left mb-6">
        <h2 className="text-3xl font-bold text-[#092F55]">Authenticate</h2>
      </div>

      <div className="space-y-6">
        {/* Header with Icon */}
        <div className="flex flex-col mb-2">
          <p className="text-md text-[#171717]">
            Enter the code from your authenticator app.
          </p>

          <p className="text-md text-[#171717] !mt-5">
            Code will expire in 2 minutes.
          </p>
        </div>

        {/* MFA Verification Form */}
        <form className="space-y-4">
          <div className="space-y-2">
            <div className="flex gap-3">
                {digits.map((digit, idx) => (
                  <Input
                    key={idx}
                    ref={el => inputsRef.current[idx] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={e => handleChange(idx, e.target.value)}
                    onKeyDown={e => handleKeyDown(idx, e)}
                    onPaste={handlePaste}
                    disabled={loading}
                    className={`!bg-white ${error ? 'border-red-500' : 'border-[#EBEBEB]'} h-20 w-14 text-center text-2xl tracking-widest font-mono`}
                    autoFocus={idx === 0}
                  />
                ))}
            </div>

            <p className="text-sm text-red-700 mt-1">{error}</p>

            <p className="text-md text-[#171717] !mt-5">
            Didn't receive a code? <a href="#" className="text-[#0C3E71] font-semibold">Resend code</a>
            </p>
            
            <div className="!mt-5">    
              <a href="#" className="text-[#0C3E71] font-semibold mt-4">Try another method</a>
            </div>

          </div>
        </form>

      </div>

      <div className="flex space-x-4 mt-auto pt-6">
        <Button 
          type="button" 
          variant="secondary"
          mode="stroke"
          className="flex-1 !border-border-light !font-normal"
          disabled={loading}
          onClick={() => {setAuthType("signin"); navigate(ROUTES.HOME.path);}}
        > 
          Cancel
        </Button>
        <Button 
          type={'button'}
          className="flex-1 !font-normal bg-primary-500 hover:bg-primary-600 disabled:bg-[#F7F7F7] disabled:border disabled:border-[#EBEBEB] disabled:text-gray-400"
          disabled={digits.every(d => d === "") || loading}
          onClick={verifyMFASignIn}
        >
          Sign In <RiArrowRightLine className="inline-block ml-1" />
        </Button>
      </div>
    </>
  );
}

// Default export for backward compatibility
export default function MFAVerify() {
  return <MFAVerifyForm />;
}
